import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (cb) => {
      const mql = window.matchMedia(query);
      mql.addEventListener("change", cb);
      return () => mql.removeEventListener("change", cb);
    },
    () => window.matchMedia(query).matches,
    () => false,
  );
}

export const useIsDesktop = () => useMediaQuery("(min-width: 1024px)");
export const useIsMobile = () => !useMediaQuery("(min-width: 768px)");

export function useOnline(): boolean {
  return useSyncExternalStore(
    (cb) => {
      window.addEventListener("online", cb);
      window.addEventListener("offline", cb);
      return () => {
        window.removeEventListener("online", cb);
        window.removeEventListener("offline", cb);
      };
    },
    () => navigator.onLine,
    () => true,
  );
}

export function useDocumentTitle(title: string | undefined) {
  useEffect(() => {
    document.title = title ? `${title} · LOVE CPMD` : "LOVE CPMD";
  }, [title]);
}

/** localStorage-backed state for small per-device conveniences (dismissed hints, drafts). */
export function useLocalState<T>(key: string, initial: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  const set = useCallback(
    (next: T) => {
      setValue(next);
      try {
        if (next === undefined || next === null) localStorage.removeItem(key);
        else localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* storage unavailable */
      }
    },
    [key],
  );
  return [value, set];
}

/**
 * Panels and short forms open through query params on top of the current page
 * (plan: ?session=<id>, ?action=new-schedule&date=…, ?action=change&session=<id>).
 * Opening pushes history so the browser/phone back button closes the overlay first.
 */
export function useOverlay() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const depth = (location.state as { overlay?: number } | null)?.overlay ?? 0;

  const open = useCallback(
    (patch: Record<string, string | null | undefined>) => {
      const next = new URLSearchParams(location.search);
      for (const [k, v] of Object.entries(patch)) {
        if (v === null || v === undefined) next.delete(k);
        else next.set(k, v);
      }
      navigate({ pathname: location.pathname, search: next.toString() }, { state: { overlay: depth + 1 } });
    },
    [location.pathname, location.search, navigate, depth],
  );

  const close = useCallback(
    (keys: string[]) => {
      if (depth > 0) {
        navigate(-1);
        return;
      }
      const next = new URLSearchParams(location.search);
      keys.forEach((k) => next.delete(k));
      navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
    },
    [depth, location.pathname, location.search, navigate],
  );

  return {
    params,
    sessionId: params.get("session"),
    action: params.get("action"),
    open,
    close,
    openSession: (id: string) => open({ session: id, action: null }),
    openAction: (action: string, extra: Record<string, string | null | undefined> = {}) => open({ action, ...extra }),
    closeAction: () => close(["action", "date", "target", "step"]),
    closeSession: () => close(["session", "action", "date", "target", "step"]),
  };
}
